/**
 * Keeps "You may also like" cards in sync with the colourway (and fabric
 * base) currently selected on the product being viewed.
 *
 * The recommendations section is fetched once (via the product-recommendations
 * custom element) using only the product's id, with no knowledge of whichever
 * colourway/base swatch the shopper has picked, so the cards it renders
 * always reflect that product's default variant. Shopify's variant picker
 * doesn't reload that section either; swatch selection is handled entirely
 * client-side and only updates the page's `?variant=` URL. To keep the
 * recommended cards' images matching what's actually on screen, this script
 * fetches each recommended product's own variant/image data (via
 * `/products/<handle>.js`) and swaps in the image for whichever
 * colourway + fabric base is currently selected: once on load (using the
 * values the product started on) and again whenever the shopper changes a
 * swatch, by listening for Shopify's standard `shopify:product:select` event
 * dispatched by the variant picker.
 *
 * The colourway -> image map is built here in JS, from the small per-product
 * JSON endpoint, rather than in Liquid by looping over `product.variants`.
 * Some of these fabrics have 800-1000+ variants (one per colourway/fabric
 * base/length combination), and the theme's Liquid sandbox silently cuts
 * for-loops and array filters like `where` short partway through such large
 * arrays, which produced incomplete or wrong image maps for products with
 * many variants.
 *
 * Building the map in JS from `/products/<handle>.js` avoids the Liquid
 * limit, but that endpoint's own `variants` array is also capped (at 250),
 * so for products with more variants than that, later colourways would
 * still be missing. Each product's *images* aren't one-per-variant though -
 * there are only a handful per colourway - so they stay well under that cap
 * regardless of variant count. This store's product photos are captioned
 * with the colourway (and fabric base) they show, e.g. "CW: Yellow and
 * green | BASE: Luxury Linen | HERO", so the map is built primarily from
 * parsing that, falling back to the (possibly partial) variant data only
 * for a colourway whose photos don't follow that captioning.
 *
 * A colourway can have a separate photo per fabric base (Luxury Linen,
 * Mercia Linen, Natural Luxury Linen, ...), and a photo's "HERO" tag just
 * marks a merchant-preferred default for that colourway - it isn't
 * necessarily on the same base the shopper currently has selected on the
 * product they're viewing. So the map below is keyed by colourway + base
 * where possible, and matching prefers an exact base match before falling
 * back to the colourway's HERO photo (any base) and then to any photo for
 * that colourway at all.
 *
 * Different products in this catalogue also sometimes name what is
 * effectively the same colourway slightly differently - a missing "and", a
 * stray plural, inconsistent capitalisation, a dropped-letter typo, or (in
 * one case) a genuinely different name for the same combination (Ali's
 * "Yellow and green" / Chameli's "Yellow bluey green"). An exact string
 * match between the selected product's colourway and a recommended
 * product's colourway map misses all of these. COLOURWAY_GROUPS below lists
 * sets of names that should be treated as the same colourway when matching
 * across products. It was seeded by scanning every colourway name across
 * the whole catalogue for near-duplicates (small edit distance, same
 * wording minus "and"/case/plural) and keeping only the low-risk matches -
 * pairs that were clearly the same colour described inconsistently, not
 * ones that merely looked similar (e.g. "Grey blue" and "Green blue" were
 * deliberately left ungrouped: both are real, different, widely-used
 * colourways, not a typo of each other). Add to it as more of these
 * cross-product aliases are identified.
 *
 * v11 adds a further step on top of all of the above: re-imaging a
 * recommended product only helps if that product HAS a matching colourway
 * to show in the first place. When it doesn't - not even via the aliasing
 * or single-colour fallback described above - this version searches the
 * rest of the catalog (restricted to the current page's own product type,
 * Fabric or Wallpaper) for a different product that has a better match, and
 * replaces that recommendation slot's product entirely (image, title,
 * price and links) rather than leaving a mismatched design on display. See
 * `FABRIC_CATALOG`/`WALLPAPER_CATALOG`, `classifyProductMatch`,
 * `findBestMatchInCatalog`, and the updated `applyColourway` below.
 * @type {string[][]}
 */
const COLOURWAY_GROUPS = [
  ["Blue", "Blues"],
  ["Blue and brown", "Blue brown"],
  ["Coral and grey blue and green", "Coral grey blue and green"],
  ["Dark Sage", "Dark sage"],
  ["Green", "green"],
  ["Green blue and brown", "Green blue brown", "Greeny blue and brown"],
  ["Grey green", "grey green"],
  ["Lavender and fe", "Lavender and fern"],
  ["Mid blue", "Mid blues"],
  ["Moss Green", "Moss green"],
  ["Neutral and vintage green", "Neutral vintage green"],
  ["Pale grey blue", "Pale grey bluw"],
  ["Pink and blue", "Pinks and blues"],
  ["Pink and blue green", "Pinks and blue green"],
  ["Pink and green", "Pink green", "Pinks and green"],
  ["Pinky red and grey blue", "Pinky red grey blue"],
  ["Prussian blue", "prussian blue"],
  ["Red and blue", "red and blue"],
  ["Red and green", "red and green"],
  ["Red and pink", "Red pink"],
  ["Red damson and green", "red-damson-and-green"],
  ["Rouge pink and green", "Rouge pink green", "Pink rouge and green"],
  ["Soft blue", "soft blue"],
  ["Soft dusky pink and blue", "soft dusky pink and blue"],
  ["Warm red and blue", "Warm red and blues"],
  ["Warm red and blue and green", "Warm red blue and green"],
  ["Warm red and grey blue", "warm-red-and-grey-blue"],
  ["Wild orchid pink and green", "wild-orchid-pink-and-green"],
  ["Yellow and green", "Yellow bluey green", "Yellow green"],
  ["Yellow and green white ground", "Yellow green white ground"],
];

/** @type {Map<string, string[]>} colourway name -> all names (including itself) in its group */
const colourwayGroupLookup = new Map();
for (const group of COLOURWAY_GROUPS) {
  for (const name of group) {
    colourwayGroupLookup.set(name, group);
  }
}

/**
 * Full catalog of active products and their colourway names, baked in from
 * a snapshot of the store's product data (Admin API, `products(status:active)`
 * with `productType` + `options`, taken 2026-09-18). This is what makes the
 * product-swap feature below possible: to know whether some OTHER product in
 * the catalog has a better colourway match than the one Shopify's own
 * recommendation algorithm picked, we need to know every product's colourway
 * names up front, without fetching all ~140 products' full variant data on
 * every page view.
 *
 * This is a static snapshot, not live data - a product added, renamed, or
 * given new colourways after the snapshot was taken won't be reflected here
 * until this list is regenerated and re-uploaded. A product missing from
 * here (new since the snapshot) is simply never considered as a swap
 * candidate and never has its own "does it already match" checked against
 * this data (see `applyColourway`, which falls back to the old per-product
 * live-fetched matching in that case) - so this staleness fails safe rather
 * than breaking anything.
 * @type {{ handle: string, colourways: string[] }[]}
 */
const FABRIC_CATALOG = [
  { handle: "adem", colourways: ["Warm red and grey blue", "Coral and grey blue and green", "Damson and sage", "Earth colours", "Pinks and blue green", "Pinky red and grey blue", "Red blue and green"] },
  { handle: "adem-smaller", colourways: ["Warm red and grey blue", "Coral grey blue and green", "Damson and sage", "Earth colours", "Pinks and blue green", "Pinky red and grey blue", "Red blue and green"] },
  { handle: "adhira", colourways: ["Warm red", "Baby pink", "Black", "Blue teal", "Bottle green and light ground", "Claret", "Dark denim", "Dark Sage", "Dark Teal", "Duck egg", "Fern", "Fresh green blue", "Gold", "Green blue", "Indigo on cream", "Lavender", "Light chocolate", "Lilac", "Moss Green", "Pink rouge stronger", "Pinky red and cream", "Pretty grey blue", "Racing green", "Red coral and light ground", "Red coral", "Red terracotta", "Rich red", "Sage", "Sailor blue", "Soft blue green", "Soft duck egg", "Soft pink", "Soft rose", "Summer green", "Teal on white", "Vintage soft pink", "Wild orchid pink", "Woad", "Yellow"] },
  { handle: "adhira-multicoloured", colourways: ["Red blue and sage", "Wine pink and sage"] },
  { handle: "alessia", colourways: ["Pinky red and grey blue", "Blue and moss green", "Bright blue and red", "Bright indigo", "Coral and grey blue", "Lavender and fe", "Lilac and green white ground", "Mid blue", "Neutral and soft green", "Rouge pink and green", "Soft cornflower blue and green", "Wine and sage", "Yellow green white ground", "Yellow green"] },
  { handle: "ali", colourways: ["Warm red and blues", "Blue and moss green", "Soft pink and racing green", "Yellow and green"] },
  { handle: "amara", colourways: ["Red damson and green", "Golden yellow and moss green", "Lavender and fern", "Soft pink and green", "Warm red and blue", "Wild orchid pink and green", "Yellow and moss green"] },
  { handle: "amara-simple", colourways: ["Red damson and green", "Lavender and fern", "Soft pink and green", "Wild orchid pink and green"] },
  { handle: "amara-small", colourways: ["Red and green", "Pink and green", "Soft blue and green", "Yellow and green", "Pink and green with pink ground", "Red and green blue", "Red damson and green", "Red damson and green with pink ground", "Pink and green blue"] },
  { handle: "amarini", colourways: ["Red damson with pink ground", "Red and blue green", "Red damson and green grey with blue ground", "Red damson and green grey with green ground", "Red damson and green", "Soft pink and green", "Warm red and grey blue with blue ground", "Warm red and grey blue with pink ground", "Wild orchid pink and green", "Yellow and green"] },
  { handle: "amarini-stripe", colourways: ["red-damson-and-green", "wild-orchid-pink-and-green", "warm-red-and-grey-blue"] },
  { handle: "amelie-cameo", colourways: ["Apple green and red", "Blue and pink", "Blue and red", "Green and pink", "Greeny blue and red", "Lilac and green", "Pinky red and grey blue", "Warm red"] },
  { handle: "amelie", colourways: ["Apple green blue and soft putty pink", "Apple green red and blue", "Blue red and green matching wallpaper", "Blue red and green", "Bright blue pinky red and green", "Chocolate and teal", "Damson and sage", "Dusky rose and green", "Gold red and blue", "Greens and plum", "Grey blue pinky red and green", "Grey green and soft putty pink", "Lavender and fern", "Lilac green and yellow", "Pink and blue", "Plum and green", "Rich red and green", "Soft pink blue and green", "Soft putty pink and grey green", "Teal and chocolate", "Warm red and blue and green", "Wild orchid pink and green"] },
  { handle: "amelie-stripe", colourways: ["Lilac green and yellow", "Warm red blue and green", "Apple green red and blue", "Blue and red", "Dusky rose and green", "Gold green red and blue", "Gold red and blue", "Lavender and fern", "Pink and blue", "Pinky red and grey blue"] },
  { handle: "amir", colourways: ["Azure and chocolate", "Blue and moss green", "Green and damson", "Grey blue and pinky red", "Lavender and fern", "Pink and grey green", "Sage green and red", "Soft blue and pinky red", "Soft putty pink and grey green", "Warm red and blue", "Warm red and grey blue"] },
  { handle: "anaisha", colourways: ["Green blue and brown", "Pinky red and blue", "Pinky red and grey blue", "Green blue ground"] },
  { handle: "anne", colourways: ["Pink and vintage green"] },
  { handle: "ayanna", colourways: ["Blue ground", "Damson and sage", "Pink and green", "Pinky red and blue", "Red and blue", "Red and green blue cream ground", "Soft pink ground damson and green blue", "Soft pink ground pink and green", "Soft putty pink ground pink and green", "Terracotta"] },
  { handle: "beni", colourways: ["Azure", "Blue", "Chocolate", "Coral", "Damson", "Deep red", "Dusky rose", "Golden yellow", "Green blue", "Grey blue", "Indigo blue", "Lilac on white", "Lilac", "Mid green", "Pinky red", "Racing green", "Sage", "Soft grey green", "Soft putty pink", "Tea green", "Vintage pink", "Warm red", "Yale blue"] },
  { handle: "birds-and-bees", colourways: ["Deep red and brown", "Soft red pink and blue"] },
  { handle: "bukhara-flower-stripe", colourways: ["Blue and green", "Damson and green", "Pink and blue green", "Pink and green blue", "Soft blue and green", "Soft pink and green", "Warm red and blues", "Warm red and grey blue"] },
  { handle: "bukhara-flower-stripe-smaller", colourways: ["Blue and green", "Damson and green", "Pink and blue green", "Pink and green blue", "Soft blue and green", "Soft pink and green", "Warm red and blues", "Warm red and grey blue"] },
  { handle: "bukhara", colourways: ["Blue and green", "Damson and green", "Red blue and green", "Soft pink and green"] },
  { handle: "caroline", colourways: ["Red and purple"] },
  { handle: "catherine", colourways: ["Golden yellow", "Green", "Grey blue", "Pinky red", "Red", "Simple damson and sage with texture", "Simple", "Soft pink", "Warm red"] },
  { handle: "chameli", colourways: ["Azure and chocolate", "Blue and moss green", "Bright red and blue", "Coral and fresh green", "Coral and grey blue", "Damson and sage", "Golden yellow and green", "Green gold and brown", "Lavender and fern", "Neutral and vintage green", "Peppermint and rhubarb", "Pink and duck egg", "Pinky red and blue", "Pinky red and grey blue", "Red and grey blue", "Rouge pink and green", "Soft cornflower blue and green", "Soft pink and green", "Soft pink and racing green", "Soft putty pink and grey green", "Warm red and grey blue", "Yellow bluey green", "Yellow green white ground"] },
  { handle: "chameli-simple", colourways: ["Green blue and brown", "Pink and duck egg", "Pink rouge and green", "Pinky red and blue", "Pinky red and grey blue", "Red coral and grey blue", "Soft putty pink and grey green"] },
  { handle: "chequerboard", colourways: ["Blue and moss green light ground", "Bright pink and dark green", "Red and yale blue", "Soft cornflower blue and green", "Yellow and dark green", "Yellow and moss green"] },
  { handle: "christopher-rabbit", colourways: ["Neutral stripe", "Neutral", "Pink stripe"] },
  { handle: "coromandel-stripe", colourways: ["Azure blue and chocolate", "Green gold and brown", "Green pink and brown", "Red and blue"] },
  { handle: "coromandel-stripe-smaller-scale", colourways: ["Azure blue and chocolate", "Green gold and brown", "Green pink and brown", "Red and blue"] },
  { handle: "daisy", colourways: ["Cream", "Ivory"] },
  { handle: "diamond", colourways: ["Baby pink", "Blue stone", "Crimson", "Dark sage", "Denim blue", "Duck egg", "Green blue", "Green", "Grey blue", "India yellow", "Lavender", "Lilac", "Moss green", "Ochre sage", "Petal pink", "Pretty grey blue", "Prussian blue", "Rhubarb", "Vintage soft pink", "Vintage soft putty pink", "Warm red", "Woad"] },
  { handle: "ditsy", colourways: ["Azure motif", "Azure", "Dark chocolate", "Dark red", "Deep grey green", "Deep warm red motif", "Deep warm red", "Green motif", "Green", "Grey blue motif", "Grey blue", "Grey green ground", "Grey green motif", "Indigo motif", "Indigo", "Lavender", "Pink motif", "Pink", "Pinky red motif", "Pinky red", "Soft pink motif", "Soft pink", "Warm red motif", "Warm red", "Wild orchid pink motif", "Wild orchid pink", "Yale blue"] },
  { handle: "domino-trellis", colourways: ["Blue and green", "Blue red and green", "Brown and blue", "Golden yellow and blue", "Golden yellow and green", "Pinks and green", "Red and blue"] },
  { handle: "ebru", colourways: ["Blue and brown", "Cream damson green and blue", "Cream red green and blue", "Green and red", "Green red and blue", "Red pink green and blue"] },
  { handle: "elizabeth", colourways: ["Blue and green", "Blue and moss green", "Lavender and fern", "Pinky red and grey blue white ground", "Soft pink and racing green", "Wild orchid pink and green", "Yellow and green"] },
  { handle: "elizabethan-garden", colourways: ["Lavender and fern", "Pinky red and grey blue", "Soft pink and racing green", "Warm red and grey blue", "Yellow and green", "Wine sage and blue"] },
  { handle: "ella", colourways: ["Damson", "Dusky rose", "Golden yellow", "Green", "Lavender", "Lilac on white", "Lilac", "Pinky red", "Sage", "Soft blue", "Soft pink", "Soft red", "Tea green", "Deep lilac", "Dusky pink"] },
  { handle: "enchanted-forest", colourways: ["Azure and chocolate", "Azure and chocolate white ground", "Green and chocolate natural", "Green and chocolate", "Multicoloured pink ground", "Pinky red and grey blue", "Red and blue white ground", "Red and blue", "Red and green", "Red and green match to wp"] },
  { handle: "fleur", colourways: ["Gold and green", "Grey blue gold and green", "Red and grey blue", "Red blue and green", "Red green neutral and grey blue", "Soft dusky pink and green"] },
  { handle: "flower-diamond", colourways: ["Blue and green", "Bright pink and blue", "Bright yellow and green", "Dark pink and sage", "Gold and grey blue", "Lavender and fern", "Lilac and green", "Pink and green", "Pinky red and grey blue", "Wild orchid pink and green"] },
  { handle: "georgiana", colourways: ["Indigo", "Indigo match to wallpaper", "Original white ground", "Original", "Pale blue ground", "Pale green ground", "Pale pink ground", "Pink blue and green", "Red green and blue", "Soft blue ground", "Soft Chinese yellow ground", "Soft green ground", "Soft putty pink grey green and damson", "Soft vintage", "Soft pink ground"] },
  { handle: "gingham", colourways: ["Bright green", "Golden yellow", "Green", "Grey blue", "Indigo", "Pinky red", "Racing green", "Soft pink", "Warm red", "Wild orchid pink"] },
  { handle: "gustavian-trail", colourways: ["Blue and green soft green ground", "Blue ground", "Cream ground blue and green", "Lilac and green"] },
  { handle: "indian-diamond", colourways: ["Blue and green", "Pinky red and grey blue", "Warm red and grey blue", "Yellow and green"] },
  { handle: "indian-summer-flower", colourways: ["Lilac and green", "Pink rouge and green", "Pinky red and grey blue", "Warm red and grey blue", "Yellow and green white ground"] },
  { handle: "indian-summer", colourways: ["Yellow and green white ground", "Coral and grey blue", "Pink rouge and green", "Pinky red and grey blue", "Wine and sage"] },
  { handle: "indian-summer-smaller", colourways: ["Green blue and brown", "Pink rouge and green", "Pinky red and grey blue", "Soft pink and racing green", "Yellow and green"] },
  { handle: "indian-summer-stripe", colourways: ["Green blue and brown", "Pink rouge and green", "Pinky red and grey blue", "Soft pink and racing green", "Soft putty pink and grey green", "Warm red and grey blue", "Yellow and green"] },
  { handle: "it-s-a-jungle-out-there", colourways: ["Baby blue", "Baby pink", "Multicoloured greens", "Soft blue"] },
  { handle: "jade", colourways: ["Yellow and soft green", "Bright blue and green", "Bright indigo", "Coral and grey blue", "Green blue and brown", "Lavender and fern", "Light gold and green", "Neutral and olive", "Neutral vintage green", "Pink and duck egg", "Pinky red and blue", "Red wine and sage", "Soft cornflower blue and green", "Soft pink and green", "Soft pink and racing green", "Warm red and grey blue light ground", "Warm red and grey blue", "Wild orchid pink and green", "Wine and sage", "Yellow and blue green"] },
  { handle: "jemima", colourways: ["Baby pink", "Bright yellow", "Golden yellow", "Indigo", "Lavender", "Mid blue", "Pink plum", "Prussian blue", "Soft blue", "Soft duck egg", "Soft green", "Soft greeny blue", "Soft ochre sage", "Soft plum", "Wild orchid pink"] },
  { handle: "jessica", colourways: ["Blue and moss green", "Damson and green", "Damson and sage intricate background", "Deep pink and blue", "Indienne", "Pinky red and blue with blue intricate background", "Pinky red and blue with grey blue intricate background", "Pinky red and blue", "Terracotta and blue"] },
  { handle: "jessica-palampore", colourways: ["Blue and brown", "Blue and moss green", "Coral and grey blue", "Indigo", "Pinky red and blue light ground", "Rouge pink and green", "Soft putty pink and grey green", "Warm red and grey blue", "Wine and sage", "Yellow and green light ground", "Yellow and green"] },
  { handle: "kabir", colourways: ["Black", "Blue and moss green", "Blue and red", "Blue and brown", "Bottle green and pink", "Bottle green", "Coral and grey blue", "Denim and raspberry", "Golden yellow", "Grey blue and coral", "Indienne red and purple", "Lavender and fern", "Lilac and green", "Pinky red and grey blue", "Raspberry and denim", "Red and blue", "Red and pink", "Sage and fern", "Warm red and grey blue", "Wild orchid pink and fern", "Yellow and green"] },
  { handle: "kaia", colourways: ["Mid blue", "Pinky red and blue", "Soft putty pink and grey green", "Warm red and grey blue", "Warm red"] },
  { handle: "leaf-stripe", colourways: ["Bright green", "Lilac", "Soft green"] },
  { handle: "molly", colourways: ["Bright pink", "Carmine pink", "Indigo", "Red", "Soft blue", "Soft cornflower blue", "Yellow"] },
  { handle: "monkey-palampore", colourways: ["Blue and chocolate", "Blue and moss green", "Carmine pink and green", "Coral and grey blue white ground", "Damson and green", "Deep warm red and grey blue", "Deeper warm red and grey green and grey blue", "Green blue brown", "New indigo", "Pink and duck egg", "Pink green", "Pinky red and grey blue", "Raspberry and denim", "Warm red and duck egg", "Warm red and grey green and grey blue", "Warm red and grey green", "Wild orchid pink and green", "Wine and sage", "Red and grey blue with blue ground", "Red and grey blue with red ground", "Pink grey green and damson"] },
  { handle: "nancy-stripe", colourways: ["Deep red blue and green", "Red and blue", "Toile blue"] },
  { handle: "nancy-trail-luxury-and-natural-luxury-linen", colourways: ["Toile blue", "Warm red blue and green", "Wine sage and blue"] },
  { handle: "narayan", colourways: ["Deep red blue and green", "Pinky red and grey blue"] },
  { handle: "new-ticking-stripe", colourways: ["Bright green", "Golden yellow", "Green", "Grey blue", "Indigo", "Pinky red", "Airforce blue", "Grey green", "Racing green", "Soft pink", "Warm red", "Wild orchid pink white ground", "Wild orchid pink"] },
  { handle: "nicholas-stripe", colourways: ["Crimson", "Green", "Indigo", "Mid blue", "Neutral and vintage green", "New warm red", "Pretty grey blue", "Prussian blue", "Red and cream", "Soft pink", "Warm red and neutral", "Warm red"] },
  { handle: "nina", colourways: ["Pinky red and blue", "Rouge pink green"] },
  { handle: "nitesh", colourways: ["Blue and blue green", "Blue and chocolate", "Blue and moss green", "Coral and grey blue", "Damson and green", "Green blue brown", "Lavender and fern", "Lilac and green", "Neutral and vintage green", "Pink and duck egg", "Pinky red and blue", "Pinky red and grey blue", "Pinky red and grey green", "Red and green blue", "Rouge pink green", "Soft cornflower blue and green", "Soft pink and green", "Soft pink and racing green", "Soft putty pink and grey green", "Warm red and blue light ground", "Warm red and blue pink ground", "Warm red and blue soft grey blue ground", "Warm red and blue soft pink ground", "Warm red and blue soft sage ground", "Warm red and blue with red pink ground", "Warm red and grey blue", "Wild orchid pink and green", "Yellow and green white ground", "Yellow and green"] },
  { handle: "oliver", colourways: ["Coral and grey blue", "Greeny blue and brown", "Greeny blue light and dark brown", "Indigo", "Rouge pink and green", "Warm red and grey blue"] },
  { handle: "ottoman-trail", colourways: ["Pinks and blues", "Pinky red grey blue and green", "Pinky red grey blue", "Red blue and green", "Warm red grey blue and grey green", "Warm red and grey blue"] },
  { handle: "pomegranate-trail", colourways: ["Azure blue and chocolate", "Coral and grey blue", "Lavender and fern", "Pinky red and blue", "Pinky red and grey blue", "Red and blue", "Soft cornflower blue and green", "Soft mid blue ground", "Soft pink and green", "Soft pink and racing green", "Soft putty pink and green", "Warm red and grey blue", "Wild orchid pink and green", "Yellow and green"] },
  { handle: "quercus", colourways: ["Azure blue", "Blue and dark chocolate", "Green blue", "Green", "Indienne red", "Plum", "Soft pink", "Wine"] },
  { handle: "saloni", colourways: ["Blue stone", "Duck egg", "Green blue", "Green", "Grey blue", "Mid blue light ground", "Moss green", "Pink", "Red pink", "Sage", "Sailor blue", "Soft vintage green", "Warm red new", "Warm red", "Yellow gold"] },
  { handle: "sari-stripe", colourways: ["Coral and grey blue", "Gold and green", "Green blue and brown", "Pink and duck egg", "Red and grey blue", "Soft cornflower blue and green", "Soft pink", "Warm red and grey blue white ground", "Warm red and grey blue"] },
  { handle: "sita", colourways: ["Blue and moss green", "Bottle green", "Bright indigo", "Coral and grey blue", "Denim and wine", "Denim", "Golden yellow", "Greeny blue and brown", "Grey blue and coral", "Indienne red and purple", "Light pink and green", "Lilac and green", "Pinky red and blue", "Wine and denim", "Yellow and green", "Duck egg and pink", "Green and red", "Orchid pink and green", "Pink and duck egg", "Red and blue", "Red and pink", "Warm red and grey blue"] },
  { handle: "sita-half-drop", colourways: ["Blue and moss green", "Bottle green", "Bright indigo", "Coral and grey blue", "Denim and wine", "Denim", "Duck egg and pink", "Golden yellow", "Green and red", "Green blue and brown", "Grey blue and coral", "Indienne red and purple", "Lilac and green", "Pink and green", "Pinky red and blue", "Red and blue", "Red and pink", "Warm red and grey blue", "Wild orchid pink and green", "Wine and denim", "Yellow and green"] },
  { handle: "sophia", colourways: ["Azure and chocolate", "Blue and green", "Blues", "Damson and sage", "Pink and green", "Red and blue", "Soft pink and blue", "Soft pink and green", "Soft pink", "Soft yellow and green", "Pink rouge and green"] },
  { handle: "textured-check", colourways: ["Blue", "Coral", "Damson", "Dark chocolate", "Deep red", "Dusky rose", "Fern", "Golden yellow", "Green", "Green on white", "Grey blue", "Grey green", "Indigo", "Lavender", "Lilac on white", "Lilac", "Mid blue", "Mid green", "Moss green", "Pale grey blue", "Pinky red", "Racing green", "Soft blue", "Soft grey green", "Soft putty pink", "Soft spearmint green", "Tea green", "Terracotta", "Vintage soft pink", "Warm red", "Wild orchid pink", "Yale blue", "Yellow"] },
  { handle: "theodore", colourways: ["Creamy neutral", "Dark ochre sage", "Deep red", "Grey green", "Mid greeny blue", "Soft neutral", "Yale blue"] },
  { handle: "ticking-stripe", colourways: ["Black", "Carmine pink", "Chocolate", "Coral", "Denim", "Duck egg", "Green blue", "Green", "Grey blue", "Indigo", "Lilac", "Mid blue", "Pink rouge", "Pinky red", "Prussian blue", "Sage", "Soft duck egg", "Soft olive", "Soft pink", "Warm red", "Yellow"] },
  { handle: "toby", colourways: ["Bright indigo", "Carmine pink and green", "Lavender and fern", "Pink rouge and green", "Wine and sage", "Yellow and green white ground"] },
  { handle: "tulip-trail", colourways: ["Bright indigo", "Bright yellow and green", "Neutral and soft green", "Red pink and green", "Vintage green and neutral", "Yellow and soft emerald"] },
  { handle: "turkish-trellis", colourways: ["Blue and green", "Damson and sage", "Red pink green and blue", "Red pink green and green blue", "Soft pink and green", "Soft red green and gold", "Soft pinky red green gold and blue"] },
  { handle: "vermicelli", colourways: ["Baby pink", "Black ground", "Black gold and cream", "Blue", "Bottle green", "Bright denim", "Bright pink", "Brightest pink", "Duck egg stronger", "Golden yellow", "Green blue", "Grey blue", "Grey green", "Indigo", "Lilac", "Moss green and red", "Moss green on lighter ground", "Ochre sage squiggle", "Pink", "Prussian blue", "Racing green", "Soft pink", "Warm red", "Woad"] },
  { handle: "wide-stripe", colourways: ["Dark indigo", "Dark sage", "Green", "Grey blue with cream", "Warm red", "Vintage soft pink"] },
  { handle: "wide-textured-check", colourways: ["Azure", "Blue", "Chocolate", "Coral", "Damson", "Dusky rose", "Golden yellow", "Green", "Grey blue", "Grey green", "Indigo", "Lilac", "Mid blue", "Mid green", "Pale grey bluw", "Pinky red", "Putty pink", "Racing green", "Soft blue", "Soft grey green", "Soft spearmint green", "Tea green", "Terracotta", "Vintage soft pink", "Warm red", "Wild orchid pink", "Yale blue"] },
  { handle: "zakhar", colourways: ["Damson and sage", "Green leafy background", "Indienne leafy background", "Pinky red and grey blue", "Red and blue with Ella background", "Warm red and grey blue", "Wine with green leafy background"] },
  { handle: "zakhar-smaller", colourways: ["Damson and sage", "Indienne leafy background", "Red and blue with Ella background", "Warm red and grey blue", "Wine with green leafy background"] },
  { handle: "pansy-trellis-fabric", colourways: ["Soft dusky pink and blue", "Blue and green", "Soft putty pink and green"] },
  { handle: "savannah-fabric", colourways: ["Red and blue", "Original", "Soft pink and green", "Blue", "Pink and green"] },
  { handle: "zigzag-ikat-fabric", colourways: ["Blue", "Red", "Mid blue", "Damson", "Dark green", "Golden yellow", "Moss green", "Pink on white", "Soft pink on cream"] },
  { handle: "new-ticking-stripe-fabric-copy", colourways: ["Blue", "Damson", "Golden yellow", "Dark green", "Mid blue", "Moss green", "Soft pink"] },
  { handle: "clover-trellis-fabric", colourways: ["Soft pink and grey green", "Blue", "Lavender and green"] },
  { handle: "boho-fabric", colourways: ["Red blue and green with Suzani"] },
  { handle: "hollyhock-stripe-fabric", colourways: ["Gold red and pink on green stripe", "Yellow red and pink on blue stripe", "Yellow red and pink on green stripe"] },
  { handle: "carnation-ikat-fabric", colourways: ["Lilac and green", "Soft putty pink and green", "Damson and green", "Soft pink and green", "Blue and green with green blue ground", "Soft pink and green with green blue ground", "Blue and green with cream ground", "Lilac and green with blue ground"] },
  { handle: "nancy", colourways: ["Deep red green and blue", "Pinky red and grey blue", "Toile blue", "Warm red green and blue", "Wine sage and blue", "Deep red green and blue with pink ticking stripe", "Deep red green and blue with green ticking stripe", "Deep red green and blue with blue ticking stripe", "Deep red green and blue with red ticking stripe"] },
];

/** @type {{ handle: string, colourways: string[] }[]} */
const WALLPAPER_CATALOG = [
  { handle: "adhira-multicoloured-wallpaper", colourways: ["Red blue and sage", "Soft red blue and sage"] },
  { handle: "alessia-wallpaper", colourways: ["Mid blue", "Pink and green", "Pinky red and grey blue", "Soft pink and grey green cream ground"] },
  { handle: "amara-wallpaper", colourways: ["Soft pink and green", "Wild orchid pink and green"] },
  { handle: "amara-simple-wallpaper", colourways: ["Pinky red and grey blue", "Yellow and blue"] },
  { handle: "amelie-wallpaper", colourways: ["Blue red and green", "Warm red blue and green"] },
  { handle: "amelie-stripe-wallpaper", colourways: ["Warm red and blue", "Gold green red and blue", "Grey blue and pinky red"] },
  { handle: "amir-wallpaper", colourways: ["Blue and red", "Red and blue", "Sage green and red"] },
  { handle: "birds-and-bees-wallpaper", colourways: ["Soft red pink and blue"] },
  { handle: "chameli-wallpaper", colourways: ["Pinky red and grey blue", "Pinky red and blue", "Warm red and grey blue", "Yellow and blue green", "Yellow and green", "Pink and green"] },
  { handle: "chameli-simple-wallpaper", colourways: ["Pinky red and grey blue"] },
  { handle: "chameli-smaller-wallpaper", colourways: ["Pinky red and grey blue", "Pink and green", "Warm red and grey blue", "Yellow and blue green", "Yellow and green"] },
  { handle: "coromandel-stripe-wallpaper", colourways: ["Red and blue light ground", "Green pink and brown", "Green gold and chocolate", "Green pink and brown natural", "Azure blue and chocolate", "Red and blue natural ground"] },
  { handle: "daisy-wallpaper", colourways: ["Ivory"] },
  { handle: "diamond-wallpaper", colourways: ["Pink", "Terracotta"] },
  { handle: "elizabeth-wallpaper", colourways: ["Blue and green", "Carmine pink and green"] },
  { handle: "enchanted-forest-wallpaper", colourways: ["Red and green"] },
  { handle: "flower-diamond-wallpaper", colourways: ["Mid blues", "Pinky red and grey blue"] },
  { handle: "georgiana-wallpaper", colourways: ["Soft vintage", "Original", "Chinese yellow", "Pale blue", "Pale green", "Pale pink", "Soft blue", "Soft green", "Soft pink", "Indigo"] },
  { handle: "gustavian-trail-wallpaper", colourways: ["Cream ground blue and green", "Blue and green soft green ground", "Blue ground", "Lilac and green"] },
  { handle: "indian-summer-flower-wallpaper", colourways: ["Pinky red and grey blue", "Indigo", "Warm red and grey blue"] },
  { handle: "indian-summer-stripe-wallpaper", colourways: ["Warm red and grey blue", "Indigo", "Pinky red and grey", "Blue brown"] },
  { handle: "its-a-jungle-out-there-wallpaper", colourways: ["Baby blue", "Baby pink", "Multicoloured greens"] },
  { handle: "jessica-wallpaper", colourways: ["Pinky red and grey blue"] },
  { handle: "jessica-palampore-wallpaper", colourways: ["Blue and green", "Pinky red and blue", "Pinky red grey green and blue", "Yellow and green"] },
  { handle: "jungle-stripe-wallpaper", colourways: ["Baby blue"] },
  { handle: "kabir-wallpaper", colourways: ["Red and blue", "Yellow and blue"] },
  { handle: "leaf-stripe-wallpaper", colourways: ["Soft blue", "Soft green"] },
  { handle: "molly-wallpaper", colourways: ["Baby pink"] },
  { handle: "monkey-palampore-wallpaper", colourways: ["Pinky red and blue", "Warm red grey blue and grey green", "Warm red and grey blue", "Pinky red and grey blue"] },
  { handle: "nancy-wallpaper", colourways: ["Pinky red and grey blue", "Warm red green and blue cream ground"] },
  { handle: "nancy-stripe-wallpaper", colourways: ["Red and blue"] },
  { handle: "nitesh-wallpaper", colourways: ["Pinky red and grey blue", "Warm red and grey green", "Warm red and grey blue"] },
  { handle: "pomegranate-trail-wallpaper", colourways: ["Pinky red and blue", "Pinky red and grey blue", "Warm red and grey blue", "Yellow and green"] },
  { handle: "quercus-wallpaper", colourways: ["Blue", "Indienne red"] },
  { handle: "saloni-wallpaper", colourways: ["Mid blue", "Pinky red stronger", "Pinky red"] },
  { handle: "sita-wallpaper", colourways: ["Yellow and blue", "Red and pink"] },
  { handle: "sita-half-drop-wallpaper", colourways: ["Denim", "Pinky red and grey blue", "Red and blue"] },
  { handle: "theodore-wallpaper", colourways: ["Mid blue", "Green", "Grey blue", "Grey green"] },
  { handle: "ticking-stripe-wallpaper", colourways: ["Grey blue", "Warm red"] },
  { handle: "toby-wallpaper", colourways: ["Red and green blue"] },
  { handle: "tulip-bunch-wallpaper", colourways: ["Bright indigo"] },
];

/**
 * Hand-picked "You may also like" line-ups, chosen by the merchant, that
 * override the automatic matching below for one specific product +
 * colourway. Keyed by "<product handle>|<colourway name>"; each entry lists
 * the product (and which of its colourways to picture) for each slot, in
 * order. Slots beyond the list's length are left untouched.
 * @type {Record<string, { handle: string, colourway: string }[]>}
 */
const CURATED_PICKS = {
  'sophia|Pink rouge and green': [
    { handle: 'ayanna', colourway: 'Soft pink ground pink and green' },
    { handle: 'chameli', colourway: 'Rouge pink and green' },
    { handle: 'clover-trellis-fabric', colourway: 'Soft pink and grey green' },
    { handle: 'alessia', colourway: 'Rouge pink and green' },
    { handle: 'chameli-simple', colourway: 'Pink rouge and green' },
    { handle: 'indian-summer-flower', colourway: 'Pink rouge and green' },
    { handle: 'indian-summer', colourway: 'Pink rouge and green' },
    { handle: 'jessica-palampore', colourway: 'Rouge pink and green' },
  ],
};

/** @type {Map<string, string[]>} handle -> colourway names, across both catalogs */
const ALL_PRODUCTS_BY_HANDLE = new Map();
for (const product of FABRIC_CATALOG) ALL_PRODUCTS_BY_HANDLE.set(product.handle, product.colourways);
for (const product of WALLPAPER_CATALOG) ALL_PRODUCTS_BY_HANDLE.set(product.handle, product.colourways);

/** @type {Map<string, 'Fabric' | 'Wallpaper'>} handle -> product type */
const TYPE_BY_HANDLE = new Map();
for (const product of FABRIC_CATALOG) TYPE_BY_HANDLE.set(product.handle, 'Fabric');
for (const product of WALLPAPER_CATALOG) TYPE_BY_HANDLE.set(product.handle, 'Wallpaper');

/**
 * @typedef {{ src: string, srcset?: string }} ImageEntry
 * @typedef {{
 *   byBase: Map<string, ImageEntry>,
 *   hero: ImageEntry | undefined,
 *   any: ImageEntry | undefined,
 * }} ColourwayEntry
 */

/** @type {Map<string, Promise<{ colourways: Map<string, ColourwayEntry>, variantFallback: Map<string, ImageEntry> }>>} */
const colourwayMapCache = new Map();

/**
 * @param {string} src - A Shopify CDN image URL.
 * @param {number} width
 */
function withWidth(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return src;
  }
}

/**
 * @param {string} src
 */
function buildSrcset(src) {
  return [400, 800, 1200].map((width) => `${withWidth(src, width)} ${width}w`).join(', ');
}

/**
 * @param {Map<string, ColourwayEntry>} colourways
 * @param {string} value
 */
function getOrCreateColourwayEntry(colourways, value) {
  let entry = colourways.get(value);
  if (!entry) {
    entry = { byBase: new Map(), hero: undefined, any: undefined };
    colourways.set(value, entry);
  }
  return entry;
}

/**
 * Fetches a product's variant data and builds a colourway (+ base) -> image map.
 * @param {string} handle
 */
async function fetchColourwayMap(handle) {
  /** @type {Map<string, ColourwayEntry>} */
  const colourways = new Map();
  /** @type {Map<string, ImageEntry>} */
  const variantFallback = new Map();
  // Title/url/per-colourway price, used only when this product is swapped
  // into a "You may also like" slot in place of a different one (see
  // `patchSwappedCard`) - a card that isn't being swapped never reads this.
  /** @type {{ title?: string, url?: string, colourwayPrices: Map<string, number> }} */
  const meta = { title: undefined, url: undefined, colourwayPrices: new Map() };

  try {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return { colourways, variantFallback, meta };

    const data = await response.json();
    meta.title = typeof data.title === 'string' ? data.title : undefined;
    meta.url = typeof data.url === 'string' ? data.url : `/products/${handle}`;

    // Primary source: each photo's alt text names the colourway (and
    // fabric base) it shows, e.g. "CW: Yellow and green | BASE: Luxury
    // Linen | HERO". This list stays small (a handful of photos per
    // colourway) no matter how many variants the product has, so it isn't
    // subject to the 250-item cap this endpoint applies to `variants`.
    const media = Array.isArray(data.media) && data.media.length ? data.media : data.images;
    if (Array.isArray(media)) {
      for (const item of media) {
        const alt = typeof item === 'string' ? '' : item && item.alt;
        if (!alt) continue;

        const cwMatch = alt.match(/CW:\s*([^|]+)/i);
        if (!cwMatch) continue;
        const value = cwMatch[1].trim();
        if (!value) continue;

        const imageSrc = typeof item === 'string' ? item : item.src;
        if (!imageSrc) continue;

        const isHero = /\|\s*HERO\b/i.test(alt);
        const imageEntry = { src: withWidth(imageSrc, 800), srcset: buildSrcset(imageSrc) };
        const colourwayEntry = getOrCreateColourwayEntry(colourways, value);

        const baseMatch = alt.match(/BASE:\s*([^|]+)/i);
        if (baseMatch) {
          const bases = baseMatch[1]
            .split(',')
            .map((base) => base.trim())
            .filter(Boolean);
          for (const base of bases) {
            colourwayEntry.byBase.set(base, imageEntry);
          }
        }

        if (isHero) {
          colourwayEntry.hero = imageEntry;
        }
        // Last photo encountered for a colourway (of any base) wins as the
        // catch-all "any" fallback, unless we already have a HERO photo -
        // the HERO one is a better generic default than an arbitrary base.
        if (!colourwayEntry.hero || isHero) {
          colourwayEntry.any = imageEntry;
        }
      }
    }

    // Fallback source: match by variant option value. Only needed for a
    // colourway whose photos don't follow the "CW: ..." captioning above -
    // this can miss later colourways on products with 250+ variants, since
    // `variants` here is capped, but it's better than nothing. This
    // fallback doesn't attempt base-matching.
    const options = Array.isArray(data.options) ? data.options : [];
    // `/products/<handle>.js` has returned options as plain name strings in
    // some Shopify API versions and as `{ name, position, values }` objects
    // in others; handle both rather than assuming one shape.
    const colourwayIndex = options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option && option.name;
      return typeof name === 'string' && name.toLowerCase() === 'colourway';
    });

    if (colourwayIndex !== -1) {
      const optionKey = `option${colourwayIndex + 1}`;
      const variants = Array.isArray(data.variants) ? data.variants : [];

      for (const variant of variants) {
        const value = variant[optionKey];
        if (!value) continue;

        // Track the cheapest variant price seen for each colourway (a
        // colourway can span several lengths/fabric bases at different
        // prices) - used as a "from" price if this product ends up swapped
        // into a slot in place of another.
        const price = typeof variant.price === 'number' ? variant.price : Number(variant.price);
        if (Number.isFinite(price)) {
          const existingPrice = meta.colourwayPrices.get(value);
          if (existingPrice === undefined || price < existingPrice) {
            meta.colourwayPrices.set(value, price);
          }
        }

        if (colourways.has(value) || variantFallback.has(value)) continue;

        const imageSrc = variant.featured_image && variant.featured_image.src;
        if (imageSrc) {
          variantFallback.set(value, { src: withWidth(imageSrc, 800), srcset: buildSrcset(imageSrc) });
        }
      }
    }

    // Last resort: colourways that matched nothing above fall back to the
    // product's main image, so they still show something sensible.
    const fallbackSrc = data.featured_image || (Array.isArray(data.images) ? data.images[0] : null);
    if (fallbackSrc && colourways.size === 0 && variantFallback.size === 0) {
      variantFallback.set('*', { src: withWidth(fallbackSrc, 800), srcset: buildSrcset(fallbackSrc) });
    }
  } catch (error) {
    // Leave the maps empty (or partially filled) - callers treat a missing
    // entry as "nothing to swap", so this fails safe.
  }

  return { colourways, variantFallback, meta };
}

/**
 * @param {string} handle
 */
function getColourwayMap(handle) {
  let mapPromise = colourwayMapCache.get(handle);
  if (!mapPromise) {
    mapPromise = fetchColourwayMap(handle);
    colourwayMapCache.set(handle, mapPromise);
  }
  return mapPromise;
}

/** Words that don't themselves name a colour, ignored when tokenising a colourway name for the single-colour fallback below. */
const COLOUR_TOKEN_STOPWORDS = new Set(['and', 'with', 'ground']);

/**
 * Splits a colourway name into lowercase word tokens, dropping conjunctions
 * and "ground" (a background qualifier, not a colour itself - e.g. "white
 * ground" tokenises to just "white").
 * @param {string} value
 * @returns {string[]}
 */
function colourTokens(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((word) => word && !COLOUR_TOKEN_STOPWORDS.has(word));
}

/**
 * Last-resort match for when a recommended product has no colourway that
 * lines up with the one selected, even via COLOURWAY_GROUPS: many simpler
 * designs (a check, a zigzag, a ticking stripe) are offered in plain,
 * single-colour colourways ("Yellow", "Golden yellow", "Warm red"...), so a
 * plain colourway made up entirely of words that also appear in the
 * selected colourway's name is a reasonable stand-in - e.g. a "Yellow"
 * check for a selected "Lilac green and yellow" floral. Picks whichever of
 * the product's own colourways shares the most words this way, preferring
 * (on a tie) the one whose shared word appears earliest in the selected
 * name, since this store's naming tends to lead with the most prominent
 * colour.
 * @param {string} selectedValue
 * @param {Iterable<string>} candidateNames - a product's own colourway names.
 * @returns {string | undefined}
 */
function findSingleColourFallback(selectedValue, candidateNames) {
  const selectedTokens = colourTokens(selectedValue);
  if (!selectedTokens.length) return undefined;

  let best;
  let bestScore = -1;
  let bestPosition = Infinity;

  for (const name of candidateNames) {
    const tokens = colourTokens(name);
    if (!tokens.length) continue;
    if (!tokens.every((token) => selectedTokens.includes(token))) continue;

    const position = Math.min(...tokens.map((token) => selectedTokens.indexOf(token)));
    const score = tokens.length;
    if (score > bestScore || (score === bestScore && position < bestPosition)) {
      best = name;
      bestScore = score;
      bestPosition = position;
    }
  }

  return best;
}

/**
 * Broader than `findSingleColourFallback` above: that function requires a
 * candidate colourway's ENTIRE name to be made of words from the selected
 * colourway (a plain "Yellow" check standing in for a floral "Lilac green
 * and yellow"). This one only requires that a candidate share AT LEAST ONE
 * colour word with the selected value, in either direction - e.g. selected
 * "Azure blue and chocolate" against a candidate "Blue and brown" shares
 * "blue" even though neither name is a subset of the other. This is the
 * catalog-wide product-swap feature's last resort: when nothing anywhere
 * has a genuine (tier 1 alias or tier 2 subset) match for the selected
 * colourway, showing at least one shared colour is better than showing an
 * unrelated design, per the merchant's own instruction ("show a colourway
 * with just one or more matching colour names"). Scored by number of
 * shared words, tied-broken by how early the (best) shared word appears in
 * the selected name.
 * @param {string} selectedValue
 * @param {Iterable<string>} candidateNames
 * @returns {string | undefined}
 */
function findBestOverlapMatch(selectedValue, candidateNames) {
  const selectedTokens = colourTokens(selectedValue);
  if (!selectedTokens.length) return undefined;

  let best;
  let bestScore = 0;
  let bestPosition = Infinity;

  for (const name of candidateNames) {
    const tokens = colourTokens(name);
    if (!tokens.length) continue;

    const sharedTokens = tokens.filter((token) => selectedTokens.includes(token));
    if (!sharedTokens.length) continue;

    const position = Math.min(...sharedTokens.map((token) => selectedTokens.indexOf(token)));
    const score = sharedTokens.length;
    if (score > bestScore || (score === bestScore && position < bestPosition)) {
      best = name;
      bestScore = score;
      bestPosition = position;
    }
  }

  return best;
}

/**
 * @param {{ colourways: Map<string, ColourwayEntry>, variantFallback: Map<string, ImageEntry> }} productMap
 * @param {string} value
 * @param {string} [base]
 */
function resolveColourwayEntry(productMap, value, base) {
  const candidates = colourwayGroupLookup.get(value) || [value];

  if (base) {
    for (const candidate of candidates) {
      const entry = productMap.colourways.get(candidate);
      if (entry && entry.byBase.has(base)) return entry.byBase.get(base);
    }
  }

  for (const candidate of candidates) {
    const entry = productMap.colourways.get(candidate);
    if (entry && entry.hero) return entry.hero;
  }

  for (const candidate of candidates) {
    const entry = productMap.colourways.get(candidate);
    if (entry && entry.any) return entry.any;
  }

  for (const candidate of candidates) {
    if (productMap.variantFallback.has(candidate)) return productMap.variantFallback.get(candidate);
  }

  // Nothing named the same (or an alias of it) - try a plain single-colour
  // stand-in from this same product's own colourway list before giving up.
  const fallbackName = findSingleColourFallback(value, productMap.colourways.keys());
  if (fallbackName) {
    const entry = productMap.colourways.get(fallbackName);
    if (base && entry.byBase.has(base)) return entry.byBase.get(base);
    if (entry.hero) return entry.hero;
    if (entry.any) return entry.any;
  }

  if (productMap.variantFallback.has('*')) return productMap.variantFallback.get('*');

  return undefined;
}

/**
 * Classifies how well a product's own colourway names match the currently
 * selected colourway, against the baked-in catalog data (`FABRIC_CATALOG`/
 * `WALLPAPER_CATALOG`), for the product-swap feature. This mirrors, in
 * order of preference, the same three tiers `resolveColourwayEntry` above
 * already applies for a single (unswapped) product's own image lookup -
 * exact/alias match, then full-subset single-colour fallback - plus a new
 * third tier (`findBestOverlapMatch`) that only requires some overlap, used
 * to decide whether OTHER products in the catalog are worth swapping to.
 * @param {string} value - the selected colourway.
 * @param {string[]} colourwayNames - a candidate product's own colourway names.
 * @returns {{ tier: 1 | 2 | 3, colourwayName: string } | null}
 */
function classifyProductMatch(value, colourwayNames) {
  if (!colourwayNames || !colourwayNames.length) return null;

  const aliasCandidates = colourwayGroupLookup.get(value) || [value];
  for (const name of colourwayNames) {
    if (aliasCandidates.includes(name)) return { tier: 1, colourwayName: name };
  }

  const subsetMatch = findSingleColourFallback(value, colourwayNames);
  if (subsetMatch) return { tier: 2, colourwayName: subsetMatch };

  const overlapMatch = findBestOverlapMatch(value, colourwayNames);
  if (overlapMatch) return { tier: 3, colourwayName: overlapMatch };

  return null;
}

/**
 * Searches a type-matched catalog list (`FABRIC_CATALOG` or
 * `WALLPAPER_CATALOG`) for the best-matching product for the selected
 * colourway, excluding any handles already in use elsewhere in the same
 * "You may also like" section (so the four slots never end up recommending
 * the same product twice). Prefers the best tier available anywhere in the
 * catalog (an exact/alias match beats a subset match beats an overlap
 * match); ties within a tier are broken by catalog order, which is
 * deterministic, so re-running this on the same page state always picks
 * the same replacement.
 * @param {string} value
 * @param {{ handle: string, colourways: string[] }[]} catalogList
 * @param {Set<string>} excludeHandles
 * @returns {{ handle: string, colourwayName: string, tier: 1 | 2 | 3 } | null}
 */
function findBestMatchInCatalog(value, catalogList, excludeHandles) {
  let best = null;

  for (const product of catalogList) {
    if (excludeHandles.has(product.handle)) continue;

    const match = classifyProductMatch(value, product.colourways);
    if (!match) continue;

    if (!best || match.tier < best.tier) {
      best = { handle: product.handle, colourwayName: match.colourwayName, tier: match.tier };
      if (best.tier === 1) break; // Can't do better than an exact/alias match.
    }
  }

  return best;
}

/**
 * Works out whether the product currently being viewed is a Fabric or a
 * Wallpaper, from its handle in the URL, so a swap search only considers
 * catalog entries of the same type (per the merchant's instruction: swap
 * within fabrics only when viewing a fabric, and correspondingly for
 * wallpaper). Returns undefined for a product missing from the baked-in
 * catalog (e.g. one added since the snapshot was taken) - callers treat
 * that as "type unknown" and skip the swap feature entirely for that page,
 * rather than guessing.
 * @returns {'Fabric' | 'Wallpaper' | undefined}
 */
function getCurrentProductType() {
  const handle = getCurrentProductHandle();
  return handle ? TYPE_BY_HANDLE.get(handle) : undefined;
}

/**
 * @returns {string | undefined} the handle of the product page being viewed.
 */
function getCurrentProductHandle() {
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : undefined;
}

/**
 * Builds per-slot decisions straight from a `CURATED_PICKS` line-up. A slot
 * already showing the picked product just gets re-imaged; any other slot is
 * swapped to the picked product.
 * @param {Element[]} items
 * @param {{ handle: string, colourway: string }[]} picks
 */
function buildCuratedDecisions(items, picks) {
  return items.slice(0, picks.length).map((item, index) => {
    const pick = picks[index];
    const shownHandle = /** @type {HTMLElement} */ (item).dataset.productHandle;
    return { item, handle: pick.handle, colourwayName: pick.colourway, swapped: pick.handle !== shownHandle };
  });
}

/**
 * The third-party "Rubik Variant Images" app renders its own swatch row
 * (`.rubik-card-swatch-wrapper`) under each card asynchronously, and keeps
 * re-writing that element's own `style` attribute wholesale after it loads
 * (observed inline style like `position: relative; z-index: 1; ...` with no
 * `display` at all) - which silently wins out over a plain `el.style.display
 * = 'none'` set once right after a swap, since Rubik's own later write just
 * replaces the whole attribute. A stylesheet rule marked `!important` beats
 * a later *non*-important inline style regardless of timing, so a swapped
 * card is marked with `data-swapped-product="true"` and this one rule (added
 * to the page once) hides that card's Rubik swatches for good, however many
 * times Rubik re-renders them afterwards.
 */
function ensureSwappedCardStyles() {
  if (document.getElementById('product-swap-styles')) return;
  const style = document.createElement('style');
  style.id = 'product-swap-styles';
  style.textContent =
    '.resource-list__item[data-swapped-product="true"] .rubik-card-swatch-wrapper { display: none !important; }';
  document.head.appendChild(style);
}

/**
 * Applies the DOM changes needed when a recommendation slot is swapped to a
 * different product entirely: every link on the card, both title elements,
 * the price, and the handle used for future colourway/image lookups. The
 * third-party "Rubik Variant Images" app's own swatch row (a small strip of
 * colour thumbnails it renders under each card, independent of this
 * script) is hidden rather than updated, since it would otherwise keep
 * showing the swatches for the ORIGINAL, no-longer-displayed product - see
 * `ensureSwappedCardStyles` above for why this needs a stylesheet rule
 * rather than a one-off inline style.
 * @param {Element} item - the `.resource-list__item` card being swapped.
 * @param {{ handle: string, title?: string, url?: string, price?: number }} product
 */
function patchSwappedCard(item, product) {
  if (product.url) {
    item.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      a.setAttribute('href', product.url);
    });
  }

  if (product.title) {
    // The card carries the product title in two places: a hover/zoom
    // overlay (`h3.h4`) and the visibly-rendered title (a `.text-block`
    // whose class includes a per-block hash, e.g. "...__product_title_xyz").
    item.querySelectorAll('h3.h4, [class*="__product_title"]').forEach((el) => {
      el.textContent = product.title;
    });
  }

  if (typeof product.price === 'number') {
    const priceEl = item.querySelector('.price');
    if (priceEl) {
      // Preserve whatever currency symbol/formatting convention the theme
      // already uses (e.g. "£", "From £") rather than hardcoding one.
      const existing = priceEl.textContent || '';
      const prefixMatch = existing.match(/^[^\d]*/);
      const prefix = prefixMatch ? prefixMatch[0] : '';
      priceEl.textContent = `${prefix}${(product.price / 100).toFixed(2)}`;
    }
  }

  ensureSwappedCardStyles();
  item.querySelectorAll('.rubik-card-swatch-wrapper').forEach((el) => {
    if (el instanceof HTMLElement) el.style.display = 'none';
  });

  if (item instanceof HTMLElement) {
    item.dataset.productHandle = product.handle;
    item.dataset.swappedProduct = 'true';
  }
}

/**
 * Reads the current product's own visible Colourway/Fabric Base selection
 * directly from its variant-picker DOM, rather than trusting this
 * section's own data attributes. Those attributes come from Liquid, and
 * can be wrong in two different ways: first, `selected_or_first_available_
 * variant`, evaluated on a product *reference* rather than the page's own
 * `product` object, ignores the request's `?variant=` query parameter and
 * always resolves to the first-available variant, not whatever the
 * shopper actually has selected. Second - and this matters even once that
 * is fixed - this whole "You may also like" section is fetched a *second*
 * time asynchronously, via Shopify's Section Rendering API
 * (`routes.product_recommendations_url`), a separate HTTP request that
 * doesn't carry the shopper's `?variant=` selection either. So the
 * section's reported "initial" colourway/base can revert back to the
 * product's first-available variant the moment that async re-render
 * lands, overwriting whatever was briefly correct on the very first
 * paint. The shopper's own variant-picker, elsewhere on the page, has
 * neither problem: it's part of the product page's own render and always
 * reflects whatever is actually selected (via a deep link, a `?variant=`
 * URL, or an earlier swatch click), so reading it directly is the
 * reliable source of truth. Checking `closest('product-recommendations')`
 * is what tells this apart from a *recommended* product's own (unrelated)
 * mini swatch picker on its card.
 * @param {string} optionName
 * @returns {string | undefined}
 */
function getMainProductSelection(optionName) {
  const fieldsets = document.querySelectorAll('fieldset');
  for (const fieldset of fieldsets) {
    if (fieldset.closest('product-recommendations')) continue;
    const legend = fieldset.querySelector('legend');
    if (!legend || legend.textContent.trim() !== optionName) continue;
    const checked = fieldset.querySelector('input:checked');
    if (checked && checked.value) return checked.value;
  }
  return undefined;
}

/**
 * Beyond just re-imaging each recommended card for the selected colourway
 * (the original behaviour), this also decides - per slot - whether the
 * product Shopify's own recommendation algorithm picked is worth keeping at
 * all: if it has no genuine (tier 1 or 2) colourway match for what's
 * currently selected, the whole catalog (restricted to the current page's
 * own product type - Fabric or Wallpaper) is searched for a better one, and
 * the slot is swapped to that product instead. This is deliberately
 * conservative: a product is only replaced when something with a strictly
 * better match tier exists elsewhere, never just to shuffle designs around.
 * When nothing anywhere has better than a partial word-overlap match, the
 * best available match (which may still be the original product) is used
 * as a last resort, per the merchant's own instruction - always show
 * *some* colour connection to the selected design rather than an unrelated
 * one.
 * @param {HTMLElement} root - A `<product-recommendations>` element.
 */
async function applyColourway(root) {
  const value =
    getMainProductSelection('Colourway') || root.dataset.currentColourway || root.dataset.initialColourway;
  if (!value) return;
  const base =
    getMainProductSelection('Fabric Base') || root.dataset.currentBase || root.dataset.initialBase || undefined;

  const items = Array.from(root.querySelectorAll('.resource-list__item[data-product-handle]'));
  if (!items.length) return;

  // Only Fabric-vs-Fabric and Wallpaper-vs-Wallpaper swaps are wanted (the
  // merchant's own instruction) - when the current page's type can't be
  // determined (product missing from the baked-in catalog snapshot), skip
  // the swap feature entirely for this section and fall back to the
  // original per-product re-imaging behaviour further down.
  const currentType = getCurrentProductType();
  const catalogList = currentType === 'Fabric' ? FABRIC_CATALOG : currentType === 'Wallpaper' ? WALLPAPER_CATALOG : null;

  // Handles already occupying a slot in this section, kept up to date as
  // slots are (tentatively) reassigned below, so no two slots ever end up
  // recommending the same product.
  const usedHandles = new Set(
    items.map((item) => /** @type {HTMLElement} */ (item).dataset.productHandle).filter(Boolean)
  );

  const currentHandle = getCurrentProductHandle();
  const curatedPicks = currentHandle ? CURATED_PICKS[`${currentHandle}|${value}`] : undefined;

  const decisions = curatedPicks
    ? buildCuratedDecisions(items, curatedPicks)
    : items
    .map((item) => {
      const originalHandle = /** @type {HTMLElement} */ (item).dataset.productHandle;
      if (!originalHandle) return null;

      if (!catalogList) {
        return { item, handle: originalHandle, colourwayName: undefined, swapped: false };
      }

      const originalColourways = ALL_PRODUCTS_BY_HANDLE.get(originalHandle);
      const originalMatch = originalColourways ? classifyProductMatch(value, originalColourways) : null;

      // The originally-recommended product already has a genuine match -
      // keep it. A match on the very product Shopify's own algorithm chose
      // is always preferable to swapping, even when a same-tier match
      // exists elsewhere.
      if (originalMatch && originalMatch.tier <= 2) {
        return { item, handle: originalHandle, colourwayName: originalMatch.colourwayName, swapped: false };
      }

      const excludeForSearch = new Set(usedHandles);
      excludeForSearch.delete(originalHandle);
      const replacement = findBestMatchInCatalog(value, catalogList, excludeForSearch);

      if (!replacement || (originalMatch && replacement.tier >= originalMatch.tier)) {
        // Nothing strictly better exists anywhere else in the catalog -
        // keep the original product (possibly still with only a tier-3
        // partial match, or none at all).
        return {
          item,
          handle: originalHandle,
          colourwayName: originalMatch ? originalMatch.colourwayName : undefined,
          swapped: false,
        };
      }

      usedHandles.delete(originalHandle);
      usedHandles.add(replacement.handle);
      return {
        item,
        handle: replacement.handle,
        colourwayName: replacement.colourwayName,
        swapped: true,
      };
    })
    .filter(Boolean);

  await Promise.all(
    decisions.map(async (decision) => {
      const { item, handle, colourwayName, swapped } = decision;
      const productMap = await getColourwayMap(handle);

      if (swapped) {
        patchSwappedCard(item, {
          handle,
          title: productMap.meta && productMap.meta.title,
          url: productMap.meta && productMap.meta.url,
          price:
            productMap.meta && colourwayName && productMap.meta.colourwayPrices.has(colourwayName)
              ? productMap.meta.colourwayPrices.get(colourwayName)
              : undefined,
        });
      }

      // When we already know the exact colourway name this product matched
      // on (from the catalog-based classification above), resolve straight
      // off the live-fetched map for that name - this correctly picks up a
      // tier-3 (partial word overlap) match, which `resolveColourwayEntry`
      // doesn't attempt on its own. Otherwise (no catalog data was
      // available for this handle), fall back to the original tiered
      // lookup, unchanged from before the product-swap feature.
      let entry;
      if (colourwayName) {
        const namedEntry = productMap.colourways.get(colourwayName);
        if (namedEntry) {
          entry =
            (!swapped && base && namedEntry.byBase.has(base) && namedEntry.byBase.get(base)) ||
            namedEntry.hero ||
            namedEntry.any;
        }
        if (!entry && productMap.variantFallback.has(colourwayName)) {
          entry = productMap.variantFallback.get(colourwayName);
        }
      }
      if (!entry) {
        entry = resolveColourwayEntry(productMap, value, swapped ? undefined : base);
      }
      if (!entry || !entry.src) return;

      const img = item.querySelector('.card-gallery img.product-media__image');
      if (!(img instanceof HTMLImageElement)) return;

      img.src = entry.src;
      if (entry.srcset) {
        img.srcset = entry.srcset;
      }
    })
  );
}

function applyToAllRecommendationSections() {
  document.querySelectorAll('product-recommendations[data-product-id]').forEach((root) => {
    if (root instanceof HTMLElement) applyColourway(root);
  });
}

/**
 * Listens for the theme's standard product-select event and, when it's for
 * the product a recommendations section is attached to, updates that
 * section's cards to the newly selected colourway/base.
 * @param {Event} event
 */
function handleProductSelect(event) {
  const selectEvent = /** @type {any} */ (event);
  const selectedProduct = selectEvent.product;
  const selectedOptions = selectEvent.selectedOptions;
  if (!selectedProduct || !selectedProduct.id || !Array.isArray(selectedOptions)) return;

  const colourwayOption = selectedOptions.find((option) => option.name === 'Colourway');
  const baseOption = selectedOptions.find((option) => option.name === 'Fabric Base');
  if (!colourwayOption && !baseOption) return;

  document.querySelectorAll('product-recommendations[data-product-id]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    const productId = root.dataset.productId;
    if (!productId || !String(selectedProduct.id).endsWith(productId)) return;

    if (colourwayOption) root.dataset.currentColourway = colourwayOption.value;
    if (baseOption) root.dataset.currentBase = baseOption.value;
    applyColourway(root);
  });
}

document.addEventListener('shopify:product:select', handleProductSelect);

// The recommendations markup arrives asynchronously (and can be re-hydrated
// later), so re-apply whenever a recommendations section's contents change.
const sectionObserver = new MutationObserver(() => applyToAllRecommendationSections());

function observeRecommendationSections() {
  document.querySelectorAll('product-recommendations').forEach((root) => {
    sectionObserver.observe(root, { childList: true, subtree: true });
  });
  applyToAllRecommendationSections();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeRecommendationSections);
} else {
  observeRecommendationSections();
}